/**
 * MUI BubbleContent Component
 * 
 * This component is the Material UI equivalent of the original BubbleContent component.
 * It provides content for tooltip bubbles in the Master Designer application.
 */

import React, { useEffect, memo, Fragment, useContext } from 'react';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ReducersIndex from 'reduxModules/ducks';
import useMousePosition from 'hooks/useMousePosition';
import { getDrawingBox, getIsMeter, getCurrentDevice } from 'reduxModules/ducks/ui';
import { getArea, getCurrentValue2, getPlotProps, getRelativeAveragePlotData } from 'reduxModules/selectors/containerSelectors';
import ToolContext from 'components/services/ToolContext';
import { bilinearInterpolate } from 'utilities/mathematic';
import { isInside } from 'utilities/geometric';
import { distanceToString } from 'utilities/format';
import { isFinite, round, capitalize, values, mapValues, minBy, isEmpty, trimEnd, has, get } from 'lodash';
import { AnyAction } from '@reduxjs/toolkit';
import { 
  BubbleContentProps, 
  PositionProps, 
  LevelProps, 
  StaticProps, 
  DistanceProps 
} from 'types/components/molecules';

// Define interface for drawing box from Redux state
interface DrawingBox {
  bbox: {
    x0: number;
    y0: number;
  };
  scale: number;
  zoom: boolean;
  svgbox?: {
    top: number;
    left: number;
  };
}

// Define interface for the device data
interface DeviceData {
  modelName?: string;
  location?: {
    x?: number;
    y?: number;
    z?: number;
    [key: string]: any;
  };
  horAngle?: number;
  verAngle?: number;
  powerTap70V?: number;
  gain?: number;
  delay?: number;
  [key: string]: any;
}

// Define interface for area data
interface AreaData {
  id?: string;
  name?: string;
  location?: {
    left: number;
    top: number;
  };
  dimensions?: {
    length: number;
    width: number;
  };
  speakerLayout?: any[];
  [key: string]: any;
}

/**
 * Styled component for the wrapper
 */
const StyledBox = styled(Box)(({ theme }) => ({
  fontSize: '10px',
  padding: '0px',
}));

/**
 * MUI Position component
 * 
 * Renders the position and details of a device at the current mouse position.
 * This component is memoized for performance optimization.
 */
const MuiPosition = memo<PositionProps>(() => {
  const { bbox: { x0, y0 }, scale, zoom, svgbox = { top: 0, left: 0 } } = useSelector(getDrawingBox) as DrawingBox;
  let deviceType = useSelector(getCurrentDevice) as string;
  /**
   * @note: The current implementation of CRD uses a single camera so the menu item is 'camera' while the object is ready to support 
   * multiple cameras. Once that feature is enables and the menu item (label) changes to 'cameras' this override will not be necesary 
   */
  deviceType = `${deviceType}${deviceType === 'camera' ? 's' : ''}`;
  // Cast the result to the expected type with x and y properties
  const pos = useMousePosition() as { x: number; y: number } | null;
  const x = pos ? (pos.x - x0) / scale : 0;
  const y = pos ? (pos.y - y0) / scale : 0;

  const inArea = useSelector(getArea) as string | null;
  
  // We need to use 'as any' here because the structure is dynamic and hard to type exactly
  const areaData = useSelector((state: any) => 
    values(state.container.areas).filter((area: AreaData) => {
      return isInside(
        [x + (zoom ? area.location?.left || 0 : 0), y + (zoom ? area.location?.top || 0 : 0)], 
        { ...area.location, ...area.dimensions }
      ) && (!inArea || inArea === area.id);
    }).pop() ?? {}
  ) as AreaData;

  const { id, location: areaOrigin = { left: 0, top: 0 }, name: areaName = '', [deviceType]: devices = {} } = areaData;

  const deviceContainer = document.getElementById(`${id}-${deviceType}`);
  const device = Array.from(deviceContainer ? deviceContainer.children : []).find(child => 
    pos && isInside([pos.x + svgbox.left, pos.y + svgbox.top], child?.getBoundingClientRect() || {})
  )?.id;

  const [area, , deviceId] = device ? device.split('-') : [];
  const deviceData = get(devices, has(devices, 'layout') ? `layout[${deviceId}]` : deviceId) as DeviceData | undefined;

  const dispatch = useDispatch();

  /**
   * Effect to update bubble content visibility based on device data and area.
   */
  useEffect(() => {
    if ((!inArea || inArea === area) && deviceData) {
      dispatch(ReducersIndex.uiReducers.setBubbleContent(true) as unknown as AnyAction);
    } else {
      dispatch(ReducersIndex.uiReducers.setBubbleContent(false) as unknown as AnyAction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceData, x0, y0, svgbox.top, svgbox.left, scale, inArea, areaOrigin, zoom, dispatch, area]);

  const isMeter = useSelector(getIsMeter) as boolean;

  if (!deviceData) return null;

  const { modelName, location, horAngle, verAngle, powerTap70V, gain, delay } = deviceData;
  const deviceLocation = location ? 
    mapValues(location, (distance) => distance ? distanceToString(distance, isMeter) : null) : 
    {};
  const devId = `${areaName} ${deviceType.substring(0, deviceType.length - 1)} ${+deviceId + 1}`;

  return (
    <StyledBox>
      <Typography variant="body2">{capitalize(devId)}</Typography>
      <Typography variant="body2">Model: {modelName}</Typography>
      <Typography variant="body2">X: {deviceLocation.x}, Y: {deviceLocation.y}{deviceLocation.z ? `, Z: ${deviceLocation.z}` : ''}</Typography>
      {horAngle !== undefined && <Typography variant="body2">Hor Angle: {horAngle}deg</Typography>}
      {verAngle !== undefined && <Typography variant="body2">Ver Angle: {verAngle}deg</Typography>}
      {powerTap70V !== undefined && <Typography variant="body2">70V Tap: {powerTap70V} Watts</Typography>}
      {gain !== undefined && <Typography variant="body2">Gain: {gain} dB</Typography>}
      {delay !== undefined && <Typography variant="body2">Delay: {delay} ms</Typography>}
    </StyledBox>
  );
});

/**
 * MUI Level component
 * 
 * Renders the level information for the current mouse position in a heatmap.
 * This component is memoized for performance optimization.
 */
const MuiLevel = memo<LevelProps>(() => {
  const { bbox: { x0, y0 }, scale, zoom } = useSelector(getDrawingBox) as DrawingBox;

  /**
   * Calculates the mouse position relative to the drawing area
   */
  // Cast the result to the expected type
  const pos = useMousePosition() as { x: number; y: number } | null;
  const x = pos ? (pos.x - x0) / scale : 0;
  const y = pos ? (pos.y - y0) / scale : 0;

  /**
   * Selects the current area based on mouse position
   */
  const inArea = useSelector(getArea) as string | null;
  
  // We need to use 'as any' for complex Redux state types
  const areaData = useSelector((state: any) => 
    values(state.container.areas).filter((area: AreaData) => {
      return isInside(
        [x + (zoom ? area.location?.left || 0 : 0), y + (zoom ? area.location?.top || 0 : 0)], 
        { ...area.location, ...area.dimensions }
      ) && (!inArea || inArea === area.id);
    }).pop() ?? {}
  ) as AreaData;

  const { id, location = { left: 0, top: 0 }, speakerLayout } = areaData;

  /**
   * Retrieves plot properties from Redux store
   */
  const { label, source, units, roundTo = 1 } = useSelector(getPlotProps) as {
    label?: string;
    source?: string;
    units?: string;
    roundTo?: number;
    [key: string]: any;
  };
  
  // Type assertions for Redux state data
  let heatmap: any = useSelector(getRelativeAveragePlotData);
  let mapArray = heatmap?.mapArray;

  /**
   * Calculates the pixel level at the current mouse position
   */
  let pixelLevel: number | undefined;
  if (id) {
    if (source && speakerLayout) {
      heatmap = speakerLayout?.filter((map: any) => 
        isInside([x + (zoom ? location.left : 0), y + (zoom ? location.top : 0)], { ...map.bbox })
      ).pop();
      mapArray = heatmap?.[source];
    }
    
    if (mapArray) {
      pixelLevel = bilinearInterpolate(
        { x: x - (zoom ? 0 : location.left), y: y - (zoom ? 0 : location.top) }, 
        mapArray, 
        heatmap.receiverSpacing, 
        heatmap.receiverXOffset, 
        heatmap.receiverYOffset
      ) - (source ? 0 : heatmap.averageTarget);
    }
  }

  /**
   * Effect to update bubble content visibility based on pixel level
   */
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      ReducersIndex.uiReducers.setBubbleContent(
        isFinite(pixelLevel as number) ? Boolean(pixelLevel) : false
      ) as unknown as AnyAction
    );
  }, [pixelLevel, dispatch]);

  return (
    <Fragment>
      {isFinite(pixelLevel as number) && (
        <StyledBox>
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {`${label}: ${round(pixelLevel as number, roundTo)} `}
            <span style={{ textTransform: 'initial' }}>{units}</span>
          </Typography>
        </StyledBox>
      )}
    </Fragment>
  );
});

/**
 * MUI Static component
 * 
 * Renders static content for the bubble.
 */
const MuiStatic = React.forwardRef<HTMLDivElement, StaticProps>((props, ref) => {
  return (
    <StyledBox ref={ref} {...props}>
    </StyledBox>
  );
});

/**
 * MUI Distance component
 * 
 * A memoized component that displays the distance to the nearest device (camera/microphone)
 * from the current mouse position.
 */
const MuiDistance = memo<DistanceProps>(() => {
  const { bbox: { x0, y0 }, zoom, scale } = useSelector(getDrawingBox) as DrawingBox;
  let deviceType = useSelector(getCurrentDevice) as string;
  deviceType = `${deviceType}${deviceType === 'camera' ? 's' : ''}`;
  
  // Helper function to safely call getCurrentValue2 with TypeScript
  const safeGetValue = (state: any, param: string) => {
    // Cast the function to allow any number of arguments
    const getValue = getCurrentValue2 as any;
    return getValue(state, param);
  };

  // We need to use 'as any' for complex Redux state
  const currentDevice = useSelector((state: any) => safeGetValue(state, 'selectedDevice')) as string | null;
  const context = useContext(ToolContext);
  const micHeight = useSelector((state: any) => safeGetValue(state, 'microphones.height')) as number;
  const talkerHeight = (context.constants?.area?.talkerHeight as number) || 0;
  const isMic = deviceType === 'microphones';
  // Cast the result to the expected type
  const pos = useMousePosition() as { x: number; y: number } | null;
  const x = pos ? (pos.x - x0) / scale : 0;
  const y = pos ? (pos.y - y0) / scale : 0;
  const z = isMic ? micHeight - talkerHeight : 0;

  const inArea = useSelector(getArea) as string | null;
  
  // Type assertion for Redux state
  const areaData = useSelector((state: any) => 
    values(state.container.areas).filter((area: AreaData) => {
      return isInside(
        [x + (zoom ? area.location?.left || 0 : 0), y + (zoom ? area.location?.top || 0 : 0)], 
        { ...area.location, ...area.dimensions }
      ) && (!inArea || inArea === area.id);
    }).pop() ?? {}
  ) as AreaData;

  const { id, [deviceType]: devices = {} } = areaData;
  const layout = (devices as any)?.layout || [];

  // Find the closest device
  const closestDevice = isEmpty(layout) ? null : minBy(layout, (device: any) => 
    Math.hypot(device.location.x - x, device.location.y - y)
  );
  
  const deviceId = id && closestDevice ? `${id}-${deviceType}-${layout?.indexOf(closestDevice)}` : null;
  const distance = closestDevice ? 
    Math.hypot(closestDevice.location.x - x, closestDevice.location.y - y, z) : 
    0;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      ReducersIndex.uiReducers.setBubbleContent(
        distance > 0.1 ? Boolean(distance) : false
      ) as unknown as AnyAction
    );
    
    if (deviceId !== currentDevice) {
      dispatch(
        ReducersIndex.uiReducers.setSelectedDevice(deviceId) as unknown as AnyAction
      );
    }
  }, [id, distance, deviceId, currentDevice, dispatch]);

  const isMeter = useSelector(getIsMeter) as boolean;

  return (
    <StyledBox>
      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
        {`${isMic ? 'talker ' : ''}distance to nearest ${trimEnd(deviceType, 's')}: ${distanceToString(distance, isMeter)} `}
      </Typography>
    </StyledBox>
  );
});

// Set display names for debugging
MuiPosition.displayName = 'MuiPosition';
MuiLevel.displayName = 'MuiLevel';
MuiStatic.displayName = 'MuiStatic';
MuiDistance.displayName = 'MuiDistance';

/**
 * MUI bubbles object
 * 
 * Contains references to all bubble content components.
 */
/**
 * MUI BubbleContent component that serves as a container for different bubble types
 */
const MuiBubbleContent: React.FC<BubbleContentProps> = ({ type = 'level' }) => {
  // Map of bubble types to their components
  const bubbleTypes: Record<string, React.ComponentType<any>> = {
    Static: MuiStatic,
  };

  // Get the component for the requested type, defaulting to MuiLevel
  const Component = bubbleTypes[type] || MuiLevel;
  
  // Render the appropriate bubble component
  return <Component />;
};

// Add display name for debugging
MuiBubbleContent.displayName = 'MuiBubbleContent';

// For backwards compatibility
// Note: 'position', 'level', and 'distance' are omitted — MouseHandler handles them via SVG overlays.
const muiBubbles: Record<string, { element: React.ComponentType<any> }> = {
  Static: {
    element: MuiStatic,
  },
};

export type {
  BubbleContentProps,
  PositionProps,
  LevelProps,
  StaticProps,
  DistanceProps
};
export { MuiPosition, MuiLevel, MuiStatic, MuiDistance, muiBubbles };
export default MuiBubbleContent;
